/**
 * @module app.core
 */

import { autoinject } from 'aurelia-framework';
import { ComponentBase, Sorting, SortDirection, SortEntry } from 'app/shared/lib/essette-site';
import { apiCall } from 'app/shared/lib/api/api';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { TransitionalManager } from 'app/shared/lib/transitional/manager';
import { IMemberSearchFilters, MemberService } from 'app/cd/services/member-service';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { IPopup } from 'app/shared/elements/popup-base';
import { validate } from 'app/shared/elements/ess-rule';
import { FormSuccessCustomEvent } from 'app/shared/elements/forms/ess-form';
import { CustomFieldContext } from 'app/core/lib/custom-field-context';
import { ICustomFieldTargetColumnsContainer } from 'app/shared/lib/custom-field-definitions';
import * as CD from 'app/cd/models/essette-api';

@autoinject()
export class MemberSearch extends ComponentBase {
    ////****************** Fields and Properties ************************************////
    protected memberSearchFilters: IMemberSearchFilters | null = null;
    protected tempMemberSearchFilters: IMemberSearchFilters | null = null;

    protected memberResults: ReadonlyArray<CD.Member> = [];
    protected customFieldTargetColumns: ICustomFieldTargetColumnsContainer | null = null;
    protected $isValid!: boolean;
    protected validateForm = validate;

    private sorting: Sorting = new Sorting();
    protected defaultSortEntry?: SortEntry;
    protected resultCount: number = 0;
    protected pageSize: number = 25;

    ////****************** Element References ***************************************////
    protected $pager!: EssPagerCustomElement;
    protected $modal!: IPopup;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly service: MemberService,
        protected readonly transitional: TransitionalManager,
        private readonly customFieldContext: CustomFieldContext,
        ...rest: any[]) {

        super(...rest);
    }

    protected async bind(): Promise<void> {
        await this.loadMemberUDFColumns();
    }

    protected activate() {
        this.clearResults();
    }

    ////****************** Functions ************************************************////
    protected formSuccess({ detail }: FormSuccessCustomEvent): void {
        if(!!this.$modal) {
            this.$modal.close();
            this.searchMembers();
        }
    }

    /** Handles the grid's sort event */
    protected async applySort(event: SortChangeEvent): Promise<void> {
        const { property, direction } = event.detail;
        const { sorting } = this;
        if(direction === null) {
            sorting.remove(property);
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        // only trigger search if there is data to sort
        if(this.memberResults.length) await this.searchMembers();
    }

    protected clearResults() {
        this.memberResults = [];
        if(this.$pager) this.$pager.updatePage(0);
        this.resultCount = 0;
    }

    protected submitMemberSearch() {
        this.memberSearchFilters = { ...this.tempMemberSearchFilters };
        this.searchMembers();
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    protected async loadMemberUDFColumns(): Promise<void> {
        try {
            this.state.beginLoad();
            this.customFieldTargetColumns = await this.customFieldContext.getCustomFieldTargetColumns('CD.Member', { definitionCount: 25 });
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

    @apiCall
    protected async searchMembers(): Promise<void> {
        if(!this.memberSearchFilters) { return; }

        // use default sort if no other sort specified
        let sort = new Sorting();
        if(this.sorting.entries.length === 0 && this.defaultSortEntry) sort.entries.push(this.defaultSortEntry);
        else sort = this.sorting;

        const options: Partial<IQueryLoadOptions> = {
            includeInactive: false,
            top: this.pageSize,
            page: this.$pager?.page || 0,
            sorting: sort
        };

        const includes: string[] = [
            'phoneNumber.cell',
            'phoneNumber.home',
            'phoneNumber.work',
            'phoneNumber.fax',
            'gender',
            'custom',
            'custom.target.definitions'
        ];
        const fields: string[] = [
            '$display',
            'identifier.primary',
            'identifier.socialSecurityNumber',
            'name.*',
            'birthDate',
            'phoneNumber.cell.*',
            'phoneNumber.home.*',
            'phoneNumber.work.*',
            'phoneNumber.fax.*',
            'gender.description',
            'custom.*',
            'custom.target.definitions'
        ];

        try {
            this.state.beginLoad();
            const response = await this.service.search(this.memberSearchFilters, options, includes, fields);
            this.memberResults = response.results;
            this.resultCount = response.count || 0;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }
}
